import React, { useEffect, useRef, useState } from "react";

// Total design time of the opening sequence at natural speed.
// The first message stays on screen for ~2s after all three lines are in
// (t≈2600–5400ms), then fades to the studio composition.
const TOTAL_MS = 8200;

// Each event of wheel/touch input while the sequence is playing skips this
// many ms of the timeline forward. Deliberately kept small so a single scroll
// gesture (which fires many wheel events on a trackpad) can carry the visitor
// through the sequence quickly without ever feeling broken.
const SCROLL_SKIP_MS = 260;

// Fade helpers — smoothly ramp from 0→1 or 1→0 across a window on the timeline.
const easeInOut = (x) => (x < 0.5 ? 2 * x * x : 1 - Math.pow(-2 * x + 2, 2) / 2);
const fadeIn = (t, start, dur) =>
  easeInOut(Math.min(1, Math.max(0, (t - start) / dur)));
const fadeOut = (t, start, dur) => 1 - fadeIn(t, start, dur);

export const Hero = () => {
  const [t, setT] = useState(0); // elapsed ms on the design timeline
  const [done, setDone] = useState(false);
  const startRef = useRef(0);
  const rafRef = useRef(0);

  // Drive the timeline with requestAnimationFrame.
  useEffect(() => {
    startRef.current = performance.now();
    const tick = (now) => {
      const elapsed = now - startRef.current;
      const clamped = Math.min(TOTAL_MS, Math.max(0, elapsed));
      setT(clamped);
      if (clamped < TOTAL_MS) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        setDone(true);
      }
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, []);

  // While the opening sequence is playing: swallow scroll and accelerate the
  // timeline instead of moving the page. When it completes: release scroll.
  useEffect(() => {
    if (done) return undefined;

    const accelerate = (e) => {
      // Never trap scroll if the user has already left the top of the page
      // (e.g. hot-reload during dev). Playing safe.
      if (window.scrollY > 0) return;
      e.preventDefault();
      // Move startRef backwards so `now - startRef` grows faster.
      const now = performance.now();
      const minStart = now - TOTAL_MS;
      startRef.current = Math.max(minStart, startRef.current - SCROLL_SKIP_MS);
    };

    const onKey = (e) => {
      if (
        [" ", "PageDown", "ArrowDown", "End", "PageUp", "ArrowUp", "Home"].includes(
          e.key
        )
      ) {
        e.preventDefault();
        const now = performance.now();
        const minStart = now - TOTAL_MS;
        startRef.current = Math.max(minStart, startRef.current - SCROLL_SKIP_MS * 3);
      }
    };

    window.addEventListener("wheel", accelerate, { passive: false });
    window.addEventListener("touchmove", accelerate, { passive: false });
    window.addEventListener("keydown", onKey);

    // Lock scroll during the intro without touching the html element. This
    // preserves `scrollbar-gutter: stable` on <html>, so the scrollbar gutter
    // stays reserved across the whole timeline and the layout never shifts.
    document.body.style.overflow = "hidden";

    return () => {
      window.removeEventListener("wheel", accelerate);
      window.removeEventListener("touchmove", accelerate);
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [done]);

  // --- Derived opacities from the timeline -------------------------------
  // Intro lines fade in staggered, stay visible for ~2 additional seconds,
  // then fade out together at t=5400ms.
  const introLine1 = fadeIn(t, 250, 700) * fadeOut(t, 5400, 800);
  const introLine2 = fadeIn(t, 1050, 700) * fadeOut(t, 5400, 800);
  const introLine3 = fadeIn(t, 1900, 700) * fadeOut(t, 5400, 800);

  // Black cover fades away gradually while the intro is on-screen, revealing
  // the fixed mosaic beneath.
  const coverOpacity = 1 - fadeIn(t, 300, 5400) * 0.88;

  // Studio message reveal — starts after the intro has fully faded out.
  const studioOpacity = fadeIn(t, 6300, 900);

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative w-full h-screen min-h-[720px]"
    >
      {/* Black cover — sits over the fixed poster mosaic while the intro plays. */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          background: "#080808",
          opacity: coverOpacity,
          zIndex: 1,
          transition: "opacity 60ms linear",
        }}
      />

      {/* Full-viewport cinematic vignette — soft, edgeless, ensures the mosaic
          never fights the copy while avoiding any hard rectangular block. */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          zIndex: 2,
          background:
            "radial-gradient(ellipse 70% 60% at 50% 50%, rgba(8,8,8,0.88) 0%, rgba(8,8,8,0.7) 35%, rgba(8,8,8,0.35) 65%, rgba(8,8,8,0.1) 88%, rgba(8,8,8,0) 100%)",
        }}
      />

      {/* STATE 1 — the intro sequence */}
      <div
        className="absolute inset-0 flex flex-col items-center justify-center px-6 md:px-12 text-center"
        style={{ zIndex: 10 }}
        data-testid="hero-intro"
      >
        <div
          style={{
            fontSize: "clamp(2.5rem, 6.5vw, 5.75rem)",
            letterSpacing: "-0.045em",
            fontWeight: 500,
            opacity: introLine1,
            lineHeight: 0.95,
          }}
          data-testid="hero-line-3s"
        >
          <span style={{ color: "#FF6A3D" }}>3</span>
          <span style={{ color: "#F5F5F3" }}> seconds.</span>
        </div>

        <div
          className="mt-8 md:mt-10"
          style={{
            fontSize: "clamp(1.75rem, 4vw, 3rem)",
            letterSpacing: "-0.03em",
            fontWeight: 500,
            color: "#F5F5F3",
            opacity: introLine2,
          }}
          data-testid="hero-line-1"
        >
          To stop the swipe.
        </div>

        <div
          className="mt-8 md:mt-10 max-w-3xl"
          style={{
            fontSize: "clamp(1.5rem, 2.6vw, 2.3rem)",
            fontWeight: 500,
            color: "#F5F5F3",
            letterSpacing: "-0.02em",
            opacity: introLine3,
            lineHeight: 1.15,
          }}
          data-testid="hero-line-2"
        >
          We build stories worth staying for.
        </div>
      </div>

      {/* STATE 2 — the Livingroom composition.
          Rendered from mount with opacity 0 so its layout is stable and there
          is no horizontal shift when it becomes visible. */}
      {/* STATE 2 — the Livingroom composition.
          Structural centering: the overlay covers the full hero viewport and
          uses flex to place the copy block in the exact centre. Each line is
          its own block, centred independently — no dependence on the mosaic
          grid or any padded container. */}
      <div
        className="absolute inset-0 flex items-center justify-center"
        style={{
          zIndex: 11,
          opacity: studioOpacity,
          pointerEvents: studioOpacity > 0.4 ? "auto" : "none",
          contain: "layout",
        }}
        data-testid="hero-studio"
      >
        <div
          className="flex flex-col items-center"
          style={{
            width: "min(90vw, 900px)",
            textAlign: "center",
          }}
        >
          {/* Livingroom wordmark — same style as the nav brand. */}
          <div
            className="leading-[0.9]"
            style={{
              fontSize: "clamp(3.5rem, 9vw, 8rem)",
              letterSpacing: "-0.05em",
              fontWeight: 500,
              color: "#F5F5F3",
              margin: 0,
              textAlign: "center",
            }}
            data-testid="hero-brand"
          >
            Livingroom<span style={{ color: "#FF6A3D" }}>.</span>
          </div>

          <div
            style={{
              marginTop: "clamp(1.25rem, 1.8vw, 1.75rem)",
              marginInline: "auto",
              fontSize: "clamp(0.7rem, 0.85vw, 0.82rem)",
              letterSpacing: "0.34em",
              textTransform: "uppercase",
              fontWeight: 500,
              color: "#A1A1AA",
              lineHeight: 1,
              textAlign: "center",
            }}
            data-testid="hero-est"
          >
            Est. in Poland
          </div>

          <p
            className="leading-[1.15]"
            style={{
              fontSize: "clamp(1.15rem, 2vw, 1.75rem)",
              letterSpacing: "-0.02em",
              fontWeight: 500,
              color: "#F5F5F3",
              margin: 0,
              marginTop: "clamp(2.5rem, 4vw, 4rem)",
              marginInline: "auto",
              maxWidth: "min(92vw, 640px)",
              width: "100%",
              textAlign: "center",
            }}
            data-testid="hero-lead"
          >
            Global entertainment.
            <br />
            For every screen.
          </p>
        </div>
      </div>
    </section>
  );
};

export default Hero;
